import React from "react";
import Dimension from "../../models/Dimension";
import LocationInfo from "../../models/LocationInfo";
import {MapControlPosition} from "../../components/map/ControlsLayer"
import MapWrapper from "../../components/map/MapWrapper"
import MarkerInfo from "../../models/MarkerInfo";
import {AnchorState} from "../../models/MarkerInfo";
import Anchor from "../../models/Anchor";
import AnchorService from "../../services/AnchorService"
import GeolocationService from "../../services/GeolocationService";
import Geolocation from "../../models/Geolocation"
import NativeInterface from "../../services/NativeInterface";
import { Form } from 'react-bootstrap';

enum ModeEnum {
    View = 'View',
    Edit = 'Edit',
    TrackingStatus = 'TrackingStatus'
}

enum AnchorTypeEnum {
    All = 'All',
    PointCloud = 'PointCloud',
    Marker = 'Marker',
    MarkerNoLocation = 'Marker (no gps)',
}

type AnchorInfo = {
    anchor: Anchor,
    state: AnchorState,
    stateStr?: string
};
type AnchorDict = Record<string, AnchorInfo>;

type LatLng = {
    geolocation: {
        lat: number,
        lng: number
    }
}
type EditedAnchorDict = Record<string, LatLng>;

function strHashCode(s: string): number {
    let hash = 0;
    if (s.length === 0) return hash;
    for (let i = 0; i < s.length; i++) {
        const char = s.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

function colorIconByLocation(location: string) {
    let colorCode = strHashCode(location) % 54 + 1
    if (colorCode <= 0 || colorCode >= 55) {
        console.error(`BAD ANCHOR COLOR CODE: ${colorCode}`)
    }
    return `img/cloud_anchor_colors/${colorCode.toString().padStart(2, '0')}.png`
}

function iconByState(state: AnchorState) {
    switch(state) {
        case AnchorState.Inactive:    return 'img/cloud_anchor_pin_gray.png';
        case AnchorState.Pending:     return 'img/cloud_anchor_pin_yellow.png';
        case AnchorState.Localized:   return 'img/cloud_anchor_pin_green.png';
        case AnchorState.Error:       return 'img/cloud_anchor_pin_error.png';
        case AnchorState.Edit:        return 'img/cloud_anchor_pin_edit.png';
        case AnchorState.EditPending: return 'img/cloud_anchor_pin_edit_pending.png';
        case AnchorState.None:        return 'img/cloud_anchor_pin.png';
        default:                      return 'img/cloud_anchor_pin.png';
    }
}

// Function to escape HTML special characters
function escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
}

// Function to convert a JSON object to an indented HTML string
function jsonToIndentedHtml(json: any, indentLevel: number = 0): string {
    const indent = '  '; // Change this to set the indent string (e.g., spaces or tabs)
    let html = '';
  
    if (json instanceof Date) {
        html += json.toISOString();
    } else if (Array.isArray(json)) {
      html += '[<br>';
      for (const value of json) {
        html += indent.repeat(indentLevel + 1) + jsonToIndentedHtml(value, indentLevel + 1) + ',<br>';
      }
      html += indent.repeat(indentLevel) + ']';
    } else if (typeof json === 'object' && json !== null) {
      html += '{<br>';
      for (const key in json) {
        if (json.hasOwnProperty(key)) {
          const value = json[key];
          html +=
            indent.repeat(indentLevel + 1) +
            escapeHtml(key) +
            ': ' +
            jsonToIndentedHtml(value, indentLevel + 1) +
            ',<br>';
        }
      }
      html += indent.repeat(indentLevel) + '}';
    } else if (typeof json === 'string') {
      html += `"${escapeHtml(json)}"`;
    } else {
      html += escapeHtml(String(json));
    }
  
    return html;
}

function validateAnchor(anchor: Anchor)
{
    if (!anchor.id) {
        return false
    }

    if (!anchor.envs) {
        return false
    }

    if (!anchor.file) {
        return false
    }

    return true
}

function initAnchorState(anchorInfo: AnchorInfo, mode: ModeEnum)
{
    switch (mode) {
        case ModeEnum.View: return AnchorState.None
        case ModeEnum.Edit: return AnchorState.Edit
        case ModeEnum.TrackingStatus: return AnchorState.Inactive
        default: return AnchorState.None
    }
}

function anchorsToDict(data: Anchor[])
{
    return data.reduce((acc: AnchorDict, item: Anchor) => {
        acc[item.id] = {anchor: item, state: AnchorState.None}
        return acc
    }, {})
}

const EditDimensionAnchors: React.FC<{data: Dimension}> = (props) => {
    const VALUE_ALL = 'all'
    const VALUE_ALL_ENABLED = 'all (enabled)'
    const envList = ['dev', 'prod']

    const containerRef = React.useRef<HTMLDivElement>(null);
    const editedAnchors = React.useRef<EditedAnchorDict>({})

    const [anchors, setAnchors] = React.useState<AnchorDict>({})
    const [trackingInfo, setTrackingInfo] = React.useState<any|null>(null)
    const [markers, setMarkers] = React.useState<MarkerInfo[]>([])

    const [selectedEnv, setSelectedEnv] = React.useState(VALUE_ALL)
    const [selectedLoc, setSelectedLoc] = React.useState(VALUE_ALL_ENABLED)
    const [selectedType, setSelectedType] = React.useState(AnchorTypeEnum.All)
    const [selectedMode, setSelectedMode] = React.useState(ModeEnum.View)
    
    React.useEffect(() => {
        document.title=`DimensionX Studio | Dimension Anchors`;
        editedAnchors.current = {};
        AnchorService.getAnchors({dimension: props.data.id})
            .then((data: Anchor[]) => { setAnchors(anchorsToDict(data)) })
            .catch(error => console.error(`Failed to get anchors. error: ${error}`));

        NativeInterface.registerCallback('updateTrackingStatus', (data: any) => {
            setTrackingInfo(data)
        })

        setContainerHeight()
        window.addEventListener('resize', setContainerHeight);
        return () => {
          window.removeEventListener('resize', setContainerHeight);
        };
    }, []);

    React.useEffect(() => {
        AnchorService.getAnchors({
                env: selectedEnv == VALUE_ALL ? null : selectedEnv,
                dimension: props.data.id,
                location: selectedLoc == VALUE_ALL || selectedLoc == VALUE_ALL_ENABLED ? null : selectedLoc,
                only_enabled_locations: selectedLoc == VALUE_ALL_ENABLED ? true : false,
                file_info: true,
                google_info: false // too slow
            })
            .then((data: Anchor[]) => {setAnchors(anchorsToDict(data))})
            .catch(error => console.error(`Failed to get anchors. error: ${error}`));
        if (selectedMode == ModeEnum.TrackingStatus) {
            NativeInterface.requestTrackingStatusUpdate(props.data.id)
        }
    }, [selectedEnv, selectedLoc, selectedMode]);

    React.useEffect(() => {
        updateAnchorsState()
    }, [anchors]);

    React.useEffect(() => {
        if (selectedMode != ModeEnum.TrackingStatus) {
            return
        }
        if (!trackingInfo || trackingInfo.dimension != props.data.id) {
            return
        }
        updateAnchorsState()
    }, [setTrackingInfo]);

    React.useEffect(() => {
        setContainerHeight();
    }, [containerRef]); // doesn't work for some reasons

    function setContainerHeight() {
        if (!containerRef.current) {
            return;
        }

        const computedStyle = getComputedStyle(document.documentElement);
        const smallScreenSz = parseFloat(computedStyle.getPropertyValue('--small-screen').trim());
        const mobileHeaderSz = parseFloat(computedStyle.getPropertyValue('--dx-mobile-header-height').trim());

        const top = containerRef.current.getBoundingClientRect().top;
        const wh = window.innerHeight;
        const ww = window.innerWidth;
        const availableHeight = wh - top - (ww > smallScreenSz ? 0 : mobileHeaderSz);
        containerRef.current.style.height = `${availableHeight}px`;
    };

    function getLocationName(id: string): string {
        if (props.data.locationsMap) {
            let info = props.data.locationsMap[id]
            return info ? info.name : id
        }
        return id
    }

    function updateAnchorsState() {
        for (const [id, item] of Object.entries(anchors)) {
            if (validateAnchor (item.anchor)) {
                switch (selectedMode) {
                    case ModeEnum.View:
                        item.state = AnchorState.None
                        break
                    case ModeEnum.Edit:
                        item.state = AnchorState.Edit
                        break
                    case ModeEnum.TrackingStatus:
                        if (trackingInfo && id in trackingInfo.anchors) {
                            item.state = trackingInfo.anchors[id].state
                            item.stateStr = trackingInfo.anchors[id].stateStr
                        } else {
                            item.state = AnchorState.Inactive
                        }
                        break
                    default: item.state = AnchorState.None
                        break
                }
            } else {
                item.state = AnchorState.Error
            }
        }
        updateMarkers()
    }

    function updateMarkers() {
        let rand = (new Date()).getTime().toString();
        let marks: MarkerInfo[] = [];
        for (const id in anchors) {
            let anchor = anchors[id];
            marks.push({
                id: anchor.anchor.id,
                pos: anchor.anchor.geolocation && (anchor.anchor.geolocation.coordinates[0] != 0 || anchor.anchor.geolocation.coordinates[1] != 0)
                     ? { lat: anchor.anchor.geolocation.coordinates[1],
                         lng: anchor.anchor.geolocation.coordinates[0] }
                     : {lat: 51.47687128794516 + Math.random() * 0.002 - 0.001,
                        lng: -0.0005056885095991286 + Math.random() * 0.002 - 0.001}, // Greenwich observatory
                icon: selectedMode == ModeEnum.View ? colorIconByLocation(anchor.anchor.location + rand) : iconByState(anchor.state),
                title: anchor.anchor.id,
                popupInfo: jsonToIndentedHtml({...{locationName: getLocationName(anchor.anchor.location)}, ...anchor.anchor, ...{ trackingState: anchor.stateStr }}),
                state: anchor.state
            });
        }
        setMarkers(marks);
    }

    function onMapClick(event: google.maps.MapMouseEvent) {
        if (!NativeInterface.isPWA() && event.latLng) {
            let loc = new Geolocation('');
            loc.latitude = event.latLng.lat()
            loc.longitude = event.latLng.lng()
            loc.horizAccuracy = 1
            GeolocationService.updateLocation(loc);
        } else if (!event.latLng) {
            console.error("Map click event has no latLng property");
        }
    };

    function envChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedEnv(e.target.value);
    }

    function locationChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedLoc(e.target.value);
    }

    function modeChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedMode(e.target.value as ModeEnum);
    }

    function typeChangeHandler(e: React.ChangeEvent<HTMLInputElement>) {
        setSelectedType(e.target.value as AnchorTypeEnum);
    }

    function onMarkerClick(markerInfo: MarkerInfo, infoWindow: google.maps.InfoWindow) {
        AnchorService.getAnchors({
            ids: [markerInfo.id],
            file_info: false,
            google_info: true//true
        })
        .then((data: Anchor[]) => {
            let popupInfo = null
            if (data.length == 1) {
                let dbAnchor = data[0]
                let anchor = anchors[dbAnchor.id]
                dbAnchor.file = anchor.anchor.file
                if (anchor) {
                    popupInfo = jsonToIndentedHtml({...{locationName: getLocationName(anchor.anchor.location)}, ...dbAnchor, ...{ trackingState: anchor.stateStr }})
                }
            }
            if (popupInfo) {
                infoWindow.setContent(`<pre style="color:black;">${popupInfo}</pre>`);
            }
        })
        .catch(error => console.error(`Failed to get anchors. error: ${error}`));
    }

    function onMarkerDrag(id: string, marker: google.maps.Marker, event: google.maps.MapMouseEvent) {
        console.log(`MARKER DRAG: ${id} - ${JSON.stringify(event)}`)
        if(event.latLng){
            editedAnchors.current[id] = {geolocation: {lat: event.latLng.lat(), lng: event.latLng.lng()}}
        } else {
            console.error("Marker drag event has no latLng");
        }
        marker.setIcon(iconByState(AnchorState.EditPending))
    }

    function createEditControls() {
        return [{
            image: 'img/ok.png',
            size: '48px',
            margin: '20px',
            position: MapControlPosition.BottomCenter,
            callback: onEditSave
        },{
            image: 'img/cancel.png',
            size: '48px',
            margin: '20px',
            position: MapControlPosition.BottomCenter,
            callback: onEditCancel
        }]
    }

    function onEditSave() {
        let cnt = Object.keys(editedAnchors.current).length
        if (cnt > 0) {
            if (!window.confirm(`Do you want to save changes: ${cnt} anchors?`)) {
                return;
            }
            AnchorService.updateAnchors(editedAnchors.current!)
            .then((res) => {
                editedAnchors.current = {}
                setSelectedMode(ModeEnum.View)
            })
            .catch(error => {
                let msg = `Failed to update anchors. error: ${error}`
                console.error(msg)
                window.alert(msg)
            });
        } else {
            setSelectedMode(ModeEnum.View)
        }
    }
    
    function onEditCancel() {
        let cnt = Object.keys(editedAnchors.current).length
        if (cnt > 0) {
            if (!window.confirm(`Do you want to discard changes: ${cnt} anchors?`)) {
                return;
            }
        }
        editedAnchors.current = {}
        setSelectedMode(ModeEnum.View)
    }

    return (
        <div ref={containerRef} style={{display: "flex", flexGrow:1, flexDirection: "column"}}>
            <div className="d-flex">
                <div className="flex-grow-1 p-3">
                    <Form.Group controlId="dim-locs-section-env">
                        <Form.Label>Environment</Form.Label>
                        <Form.Control as="select" onChange={envChangeHandler} value={selectedEnv}>
                            <option key={0} value={VALUE_ALL}>{VALUE_ALL}</option>
                            { envList
                                ? envList.map((env: string, idx: number) => (<option key={idx + 1} value={env}>{env}</option>) )
                                : "" }
                        </Form.Control>
                    </Form.Group>
                </div>
                <div className="flex-grow-1 p-3">
                    <Form.Group controlId="dim-locs-section-loc">
                        <Form.Label>Location</Form.Label>
                        <Form.Control as="select" onChange={locationChangeHandler} value={selectedLoc}>
                        <option key={0} value={VALUE_ALL}>{VALUE_ALL}</option>
                        <option key={1} value={VALUE_ALL_ENABLED}>{VALUE_ALL_ENABLED}</option>
                        { props.data.locations
                            ? props.data.locations.map((loc: LocationInfo, idx: number) => (<option key={idx + 2} value={loc.id}>{loc.name}</option>) )
                            : "" }
                        </Form.Control>
                    </Form.Group>
                </div>
                <div className="flex-grow-1 p-3">
                    <Form.Group controlId="dim-locs-section-mode">
                        <Form.Label>Mode</Form.Label>
                        <Form.Control as="select" onChange={modeChangeHandler} value={selectedMode}>
                            {Object.values(ModeEnum).map((mode: string, idx: number) => (<option key={idx + 1} value={mode}>{mode}</option>) )}
                        </Form.Control>
                    </Form.Group>
                </div>
            </div>
            <MapWrapper markers = {{
                            list: markers,
                            edit: selectedMode == ModeEnum.Edit,
                            onMarkerClickCallback: onMarkerClick,
                            onMarkerDragCallback: onMarkerDrag
                        }}
                        //onClickHandler={onMapClick}
                        controls = {{
                            buttons: selectedMode == ModeEnum.Edit ? createEditControls() : undefined
                        }}
                        geolocation = {{
                            showSearchRadius: true,
                            liveGeolocation: selectedMode != ModeEnum.TrackingStatus,
                            mockGeolocation: selectedMode == ModeEnum.TrackingStatus && trackingInfo ? trackingInfo.geolocation : undefined
                        }}
            />
        </div>
    );
};

export default EditDimensionAnchors;
